/**
 * ═══════════════════════════════════════════════════════════════════════════
 * TOOLTIP DRAWER COMPONENT - Desktop Slide-In Drawer
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * COUPLED COMPONENT - Part of 2-file tooltip system
 * ⚠️  Changes here may require updates to TooltipLabel.tsx
 *
 * RESPONSIBILITY:
 * ✓ Visual rendering of drawer UI (500px slide-in from right)
 * ✓ Backdrop click handling (onClick → onClose prop)
 * ✓ ESC key handling
 * ✓ Animation timing (300ms slide in/out)
 *
 * STATE MANAGEMENT:
 * ❌ Does NOT manage drawer open/close state
 * ✓ Receives isOpen prop from TooltipLabel.tsx
 * ✓ Receives onClose callback from TooltipLabel.tsx
 *
 * PARENT COMPONENT:
 * 📁 TooltipLabel.tsx - Manages state via DrawerManager singleton
 * 📍 Line 1077-1083: Where this component is rendered
 * 📍 Line 1079: onClose={() => DrawerManager.close()}
 *
 * DEBUGGING DRAWER CLOSE:
 * 1. Check onClick fires here (line ~110)
 * 2. Check onClose prop is correct (passed from TooltipLabel)
 * 3. Check DrawerManager.close() in TooltipLabel.tsx (line 154)
 * 4. Check subscription updates state in TooltipLabel.tsx (lines 439-444)
 *
 * ═══════════════════════════════════════════════════════════════════════════
 */

'use client';

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from '@/lib/icons';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import type { DrawerCtaConfig } from '../lib/calculator.types';
import { DrawerCtaPrimary, DrawerCtaSecondary } from './drawer-buttons';

interface TooltipDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  content: string | React.ReactNode;
  title?: string;
  ctaConfig?: DrawerCtaConfig;
}

/**
 * TooltipDrawer - Slide-in drawer for large tooltip content on desktop
 *
 * Features:
 * - Slides in from right side (500px width)
 * - Backdrop overlay with click-to-close
 * - ESC key to close
 * - X button to close
 * - Pink scrollbar matching tooltip styling
 * - Fixed positioning (stays open during page scroll)
 * - Renders via portal to document.body
 */
export const TooltipDrawer: React.FC<TooltipDrawerProps> = ({
  isOpen,
  onClose,
  content,
  title = "Learn More",
  ctaConfig
}) => {
  const [isAnimating, setIsAnimating] = React.useState(false);
  const [shouldRender, setShouldRender] = React.useState(false);

  // Apply defaults: Always show primary CTA unless explicitly disabled
  const finalCtaConfig: DrawerCtaConfig = {
    primary: {
      text: ctaConfig?.primary?.text || "Book a Strategy Call",
      href: ctaConfig?.primary?.href || "/mortgage-lead-generation-demo",
      onClick: ctaConfig?.primary?.onClick
    },
    // Site-wide default (Phase 6a): every drawer footer offers the playbook as a
    // secondary path. A caller can still override by passing its own secondary.
    secondary: ctaConfig?.secondary ?? {
      text: "Read the Complete Playbook →",
      href: "/guide/mortgage-marketing-strategies"
    },
    layout: ctaConfig?.layout || 'auto'
  };

  // Determine layout: auto-detect based on button count + text length
  const determineLayout = (): 'side-by-side' | 'stacked' => {
    // Manual override
    if (finalCtaConfig.layout === 'side-by-side' || finalCtaConfig.layout === 'stacked') {
      return finalCtaConfig.layout;
    }

    // Auto-detect
    const hasBoth = finalCtaConfig.primary && finalCtaConfig.secondary;
    if (!hasBoth) return 'side-by-side';  // Single button doesn't matter

    const primaryLength = finalCtaConfig.primary?.text?.length || 22;
    const secondaryLength = finalCtaConfig.secondary?.text?.length || 0;
    const totalLength = primaryLength + secondaryLength;

    // Stack if combined text > 35 chars
    return totalLength > 35 ? 'stacked' : 'side-by-side';
  };

  const layout = determineLayout();

  // Handle animation timing
  React.useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // Double rAF guarantees the browser paints the off-screen position
      // before the transform flips, so the open transition always runs
      let raf2: number | undefined;
      const raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setIsAnimating(true));
      });
      return () => {
        cancelAnimationFrame(raf1);
        if (raf2 !== undefined) cancelAnimationFrame(raf2);
      };
    } else {
      setIsAnimating(false);
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Handle ESC key to close drawer
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // Lock scroll when drawer is open
  useBodyScrollLock(isOpen);

  // Don't render anything if drawer should not be visible
  if (!shouldRender || typeof window === 'undefined') return null;

  // Format string content for display (similar to FullScreenTooltip)
  const formatContent = (text: string) => {
    return text.split('\n').map((line, i) => {
      // Check for no-bold marker
      const hasNoBoldMarker = line.trim().startsWith('~:');
      const cleanLine = hasNoBoldMarker ? line.trim().substring(2) : line;

      // Check if line is a title (ends with colon) - but NOT if it has the no-bold marker
      const isTitle = cleanLine.endsWith(':') && !hasNoBoldMarker;
      // Check for bullet points
      const isBullet = cleanLine.trim().startsWith('•');

      if (!cleanLine.trim()) {
        return <div key={i} className="h-2" />;
      }

      if (isTitle) {
        return (
          <h3 key={i} className="font-semibold text-white text-base mb-2 mt-4 first:mt-0">
            {cleanLine}
          </h3>
        );
      }

      if (isBullet) {
        return (
          <li key={i} className="text-gray-300 text-sm leading-relaxed mb-2 ml-4">
            {cleanLine.replace('•', '').trim()}
          </li>
        );
      }

      return (
        <p key={i} className="text-gray-300 text-sm leading-relaxed mb-2">
          {cleanLine}
        </p>
      );
    });
  };

  return createPortal(
    <div className="fixed inset-0 z-[75] bg-black/50" onClick={onClose}>
      {/* Drawer */}
      <div
        className="absolute right-0 top-0 h-screen w-[500px] bg-[#1e293b] shadow-2xl flex flex-col transform transition-transform duration-300 ease-out z-[76]"
        style={{
          transform: isAnimating ? 'translateX(0)' : 'translateX(100%)',
        }}
        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking drawer content
      >
        {/* Header with title and close button */}
        <div className="border-b border-slate-600 px-6 py-4 flex justify-between items-center flex-shrink-0">
          <h2 className="text-lg font-semibold text-white">{title}</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-700 rounded-full transition-colors"
            aria-label="Close drawer"
          >
            <X className="w-5 h-5 text-gray-300" />
          </button>
        </div>

        {/* Content area with pink scrollbar */}
        <div
          className="flex-1 overflow-y-auto px-6 py-4"
          style={{
            scrollbarColor: '#ff37a6 #1e293b',
            scrollbarWidth: 'thin',
          }}
        >
          <div className="text-gray-300 text-sm leading-relaxed">
            {typeof content === 'string' ? formatContent(content) : content}
          </div>
        </div>

        {/* Bottom CTA Buttons - Replaces close button (X in header is sufficient) */}
        <div className="border-t border-slate-600 p-4 flex-shrink-0">
          <div className={layout === 'side-by-side' ? 'flex gap-3' : 'space-y-3'}>
            {finalCtaConfig.primary && (
              <div className={layout === 'side-by-side' ? 'flex-1' : 'w-full'}>
                <DrawerCtaPrimary
                  text={finalCtaConfig.primary.text || "Book a Strategy Call"}
                  href={finalCtaConfig.primary.href}
                  onClick={finalCtaConfig.primary.onClick}
                />
              </div>
            )}
            {finalCtaConfig.secondary && (
              <div className={layout === 'side-by-side' ? 'flex-1' : 'w-full'}>
                <DrawerCtaSecondary
                  text={finalCtaConfig.secondary.text}
                  href={finalCtaConfig.secondary.href}
                  onClick={finalCtaConfig.secondary.onClick}
                  icon={finalCtaConfig.secondary.icon}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Custom scrollbar styles for webkit browsers */}
      <style jsx>{`
        div::-webkit-scrollbar {
          width: 6px;
        }

        div::-webkit-scrollbar-track {
          background: #1e293b;
        }

        div::-webkit-scrollbar-thumb {
          background: #ff37a6;
          border-radius: 3px;
        }

        div::-webkit-scrollbar-thumb:hover {
          background: #ff5ab8;
        }
      `}</style>
    </div>,
    document.body
  );
};

export default TooltipDrawer;
